import { evalCondition } from 'mailup-react-helpers'
import { type IntlShape } from 'react-intl'

import {
  type RadioButtonItem,
  type SelectGroup,
  type SelectOption,
} from '../../../helpers/types'
import {
  ActionType,
  DateFormat,
  DateMatch,
  DateTimeFormat,
  EmailMatch,
  NumericFormat,
  NumericMatch,
  SmsMatch,
  TextMatch,
} from '../../../types/FilterCondition'
import {
  UiActivityDateType,
  UiConditionType,
  UiSubscriptionConditionType,
  UiTextFormat,
  UiTrackingConditionTypes,
} from '../filterConditionTypes'

export const actionTypeOptions = (intl: IntlShape): RadioButtonItem[] => {
  return [
    {
      label: intl.formatMessage({
        id: 'Load.Filters.JoinTypeAnd',
      }),
      value: ActionType.And,
    },
    {
      label: intl.formatMessage({
        id: 'Load.Filters.JoinTypeOr',
      }),
      value: ActionType.Or,
    },
  ]
}

export const conditionTypeOptions = (intl: IntlShape): SelectOption[] => {
  const hasEngagement: boolean = evalCondition('co:DELSTATS OR co:ENGAGEMENT')

  return [
    {
      label: intl.formatMessage({
        id: 'Load.Filters.OnProfileFieldOption',
      }),
      value: UiConditionType.ProfileField,
    },
    {
      label: intl.formatMessage({
        id: 'Load.Filters.BelongsToGroupOption',
      }),
      value: UiConditionType.GroupBelongs,
    },
    {
      label: intl.formatMessage({
        id: 'Load.Filters.NotBelongsToGroupOption',
      }),
      value: UiConditionType.GroupNotBelongs,
    },
    {
      label: `${intl.formatMessage({ id: 'Load.Filters.ActivityEmailOpened' })} ${intl.formatMessage({ id: 'Load.CombinedFilterForm.EmailType' })}`,
      value: UiConditionType.EmailOpened,
    },
    {
      label: `${intl.formatMessage({ id: 'Load.Filters.ActivityEmailClicked' })} ${intl.formatMessage({ id: 'Load.CombinedFilterForm.EmailType' })}`,
      value: UiConditionType.EmailClicked,
    },
    {
      label: `${intl.formatMessage({ id: 'Load.Filters.ActivityEmailNotOpened' })} ${intl.formatMessage({ id: 'Load.CombinedFilterForm.EmailType' })}`,
      value: UiConditionType.EmailNotOpened,
    },
    {
      label: `${intl.formatMessage({ id: 'Load.Filters.ActivityEmailNotClicked' })} ${intl.formatMessage({ id: 'Load.CombinedFilterForm.EmailType' })}`,
      value: UiConditionType.EmailNotClicked,
    },
    {
      label: `${intl.formatMessage({ id: 'Load.Filters.ActivityEmailSubscribed' })} ${intl.formatMessage({ id: 'Load.CombinedFilterForm.EmailType' })}`,
      value: UiConditionType.EmailSubscribed,
    },
    {
      label: `${intl.formatMessage({ id: 'Load.Filters.ActivityEmailUnsubscribed' })} ${intl.formatMessage({ id: 'Load.CombinedFilterForm.EmailType' })}`,
      value: UiConditionType.EmailUnsubscribed,
    },
    {
      label: `${intl.formatMessage({ id: 'Load.Filters.ActivityEmailSent' })} ${intl.formatMessage({ id: 'Load.CombinedFilterForm.EmailType' })}`,
      value: UiConditionType.EmailSent,
    },
    {
      label: `${intl.formatMessage({ id: 'Load.Filters.ActivityEmailSuccessfullySent' })} ${intl.formatMessage({ id: 'Load.CombinedFilterForm.EmailType' })}`,
      value: UiConditionType.EmailSuccessfullySent,
    },
    {
      label: `${intl.formatMessage({ id: 'Load.Filters.ActivityEmailNotSent' })} ${intl.formatMessage({ id: 'Load.CombinedFilterForm.EmailType' })}`,
      value: UiConditionType.EmailNotSent,
    },
    {
      label: `${intl.formatMessage({ id: 'Load.Filters.ActivityEmailBouncedHB' })} ${intl.formatMessage({ id: 'Load.CombinedFilterForm.EmailType' })}`,
      value: UiConditionType.EmailBouncedHB,
    },
    {
      label: `${intl.formatMessage({ id: 'Load.Filters.ActivityEmailBouncedSB' })} ${intl.formatMessage({ id: 'Load.CombinedFilterForm.EmailType' })}`,
      value: UiConditionType.EmailBouncedSB,
    },
    {
      label: `${intl.formatMessage({ id: 'Load.Filters.ActivityEmailNotBouncedHB' })} ${intl.formatMessage({ id: 'Load.CombinedFilterForm.EmailType' })}`,
      value: UiConditionType.EmailNotBouncedHB,
    },
    {
      label: `${intl.formatMessage({ id: 'Load.Filters.ActivityEmailPerformedAtLeast1Action' })} ${intl.formatMessage({ id: 'Load.CombinedFilterForm.EmailType' })}`,
      value: UiConditionType.EmailPerformedAtLeast1Action,
    },
    {
      label: `${intl.formatMessage({ id: 'Load.Filters.ActivityEmailPerformedLessThan5Actions' })} ${intl.formatMessage({ id: 'Load.CombinedFilterForm.EmailType' })}`,
      value: UiConditionType.EmailPerformedLessThan5Actions,
    },
    {
      label: `${intl.formatMessage({ id: 'Load.Filters.ActivityEmailPerformedLessThan10Actions' })} ${intl.formatMessage({ id: 'Load.CombinedFilterForm.EmailType' })}`,
      value: UiConditionType.EmailPerformedLessThan10Actions,
    },
    {
      label: `${intl.formatMessage({ id: 'Load.Filters.ActivityEmailPerformed10OrMoreActions' })} ${intl.formatMessage({ id: 'Load.CombinedFilterForm.EmailType' })}`,
      value: UiConditionType.EmailPerformed10OrMoreActions,
    },
    {
      label: intl.formatMessage({ id: 'Load.Filters.ActivitySmsSent' }),
      value: UiConditionType.SmsSent,
    },
    {
      label: intl.formatMessage({ id: 'Load.Filters.ActivitySmsNotSent' }),
      value: UiConditionType.SmsNotSent,
    },
    {
      label: intl.formatMessage({
        id: 'Load.Filters.ActivitySmsSuccessfullySent',
      }),
      value: UiConditionType.SmsSuccessfullySent,
    },
    {
      label: intl.formatMessage({ id: 'Load.Filters.ActivitySmsNotReceived' }),
      value: UiConditionType.SmsNotReceived,
    },
    {
      label: intl.formatMessage({ id: 'Load.Filters.ActivitySmsExpired' }),
      value: UiConditionType.SmsExpired,
    },
    {
      label: intl.formatMessage({ id: 'Load.Filters.ActivitySmsRejected' }),
      value: UiConditionType.SmsRejected,
    },
    hasEngagement
      ? {
          label: intl.formatMessage({
            id: 'Load.NavMenu.EngagementFilters',
          }),
          value: UiConditionType.Engagement,
        }
      : undefined,
    {
      label: intl.formatMessage({
        id: 'Load.Filters.DdlActivity_Item1',
      }),
      value: UiConditionType.GeoLocatedIn,
    },
    {
      label: intl.formatMessage({
        id: 'Load.Filters.DdlActivity_Item2',
      }),
      value: UiConditionType.GeoNotLocatedIn,
    },
    {
      label: intl.formatMessage({
        id: 'Load.NavMenu.DeviceFilters',
      }),
      value: UiConditionType.Device,
    },
    {
      label: intl.formatMessage({
        id: 'Load.Filters.TrackingActive',
      }),
      value: UiTrackingConditionTypes.TrackingActive,
    },
    {
      label: intl.formatMessage({
        id: 'Load.Filters.TrackingAnonymous',
      }),
      value: UiTrackingConditionTypes.TrackingAnonymous,
    },
  ].filter((o) => o !== undefined)
}

export const conditionTypeGroups = (intl: IntlShape): SelectGroup[] => {
  return [
    {
      id: 0,
      label: intl.formatMessage({
        id: 'Load.NavMenu.ProfileFilters',
      }),
      values: [
        UiConditionType.ProfileField,
        UiConditionType.GroupBelongs,
        UiConditionType.GroupNotBelongs,
      ],
    },
    {
      id: 50,
      label: intl.formatMessage({
        id: 'Load.NavMenu.ActivityFilters',
      }),
      values: [
        ...Object.values(EmailMatch),
        ...Object.values(SmsMatch),
        ...Object.values(UiSubscriptionConditionType),
      ],
    },
    {
      id: 100,
      label: intl.formatMessage({
        id: 'Load.NavMenu.EngagementFilters',
      }),
      values: [UiConditionType.Engagement],
    },
    {
      id: 200,
      label: intl.formatMessage({
        id: 'Load.NavMenu.GeolocationFilters',
      }),
      values: [UiConditionType.GeoLocatedIn, UiConditionType.GeoNotLocatedIn],
    },
    {
      id: 300,
      label: intl.formatMessage({
        id: 'Load.NavMenu.DeviceFilters',
      }),
      values: [UiConditionType.Device],
    },
    {
      id: 400,
      label: intl.formatMessage({
        id: 'Load.Filters.Tracking',
      }),
      values: [UiTrackingConditionTypes.TrackingActive, UiTrackingConditionTypes.TrackingAnonymous],
    },
  ]
}

export const PROFILE_ADDITIONAL_FIELDS = {
  EMAIL: 'Email',
  MOBILE: 'MobileNumber',
  SUBS_DATE: 'SubscriptionDate',
}

export const formatOpts = (intl: IntlShape): SelectOption[] => [
  {
    label: intl.formatMessage({ id: 'Load.Filters.FormatTypeText' }),
    value: UiTextFormat.Text,
  },
  {
    label: intl.formatMessage({
      id: 'Load.Filters.FormatTypeInteger',
    }),
    value: NumericFormat.Integer,
  },
  {
    label: intl.formatMessage({ id: 'Load.Filters.FormatTypeDecimal' }),
    value: NumericFormat.Decimal,
  },
  {
    label: intl.formatMessage({ id: 'Load.Filters.FormatTypeDateFormat1' }),
    value: DateFormat.DateFormat1,
  },
  {
    label: intl.formatMessage({ id: 'Load.Filters.FormatTypeDateFormat2' }),
    value: DateFormat.DateFormat2,
  },
  {
    label: intl.formatMessage({ id: 'Load.Filters.FormatTypeDateFormat3' }),
    value: DateFormat.DateFormat3,
  },
  {
    label: intl.formatMessage({ id: 'Load.Filters.FormatTypeDateFormat4' }),
    value: DateFormat.DateFormat4,
  },
  {
    label: intl.formatMessage({ id: 'Load.Filters.FormatTypeDateFormat5' }),
    value: DateFormat.DateFormat5,
  },
  {
    label: intl.formatMessage({ id: 'Load.Filters.FormatTypeDateFormat6' }),
    value: DateFormat.DateFormat6,
  },
  {
    label: intl.formatMessage({ id: 'Load.Filters.FormatTypeDateFormat7' }),
    value: DateFormat.DateFormat7,
  },
  {
    label: intl.formatMessage({ id: 'Load.Filters.FormatTypeDateFormat8' }),
    value: DateFormat.DateFormat8,
  },
  {
    label: intl.formatMessage({
      id: 'Load.Filters.FormatTypeDateTimeFormat9',
    }),
    value: DateTimeFormat.DateFormat9,
  },
]

export interface MatchOption<MATCHENUM extends string | number> {
  label: string
  value: MATCHENUM
}

export const textMatchOpts = (
  intl: IntlShape,
): Array<MatchOption<TextMatch>> => [
  {
    label: intl.formatMessage({ id: 'Load.Filters.OperatorStartsWith' }),
    value: TextMatch.TextPrefix,
  },
  {
    label: intl.formatMessage({ id: 'Load.Filters.OperatorNotStartsWith' }),
    value: TextMatch.TextNotPrefix,
  },
  {
    label: intl.formatMessage({ id: 'Load.Filters.OperatorEndsWith' }),
    value: TextMatch.TextSuffix,
  },
  {
    label: intl.formatMessage({ id: 'Load.Filters.OperatorNotEndsWith' }),
    value: TextMatch.TextNotSuffix,
  },
  {
    label: intl.formatMessage({ id: 'Load.Filters.OperatorEqualsTo' }),
    value: TextMatch.TextSame,
  },
  {
    label: intl.formatMessage({ id: 'Load.Filters.OperatorNotEqualsTo' }),
    value: TextMatch.TextDifferent,
  },
  {
    label: intl.formatMessage({ id: 'Load.Filters.OperatorContains' }),
    value: TextMatch.TextContains,
  },
  {
    label: intl.formatMessage({ id: 'Load.Filters.OperatorNotContains' }),
    value: TextMatch.TextNotContains,
  },
  {
    label: intl.formatMessage({
      id: 'Load.Filters.OperatorNotNullAndNotEmpty',
    }),
    value: TextMatch.TextNotNullNotEmpty,
  },
  {
    label: intl.formatMessage({ id: 'Load.Filters.OperatorNullOrEmpty' }),
    value: TextMatch.TextNullEmpty,
  },
  {
    label: intl.formatMessage({ id: 'Load.Filters.OperatorIn' }),
    value: TextMatch.TextIn,
  },
  {
    label: intl.formatMessage({ id: 'Load.Filters.OperatorNotIn' }),
    value: TextMatch.TextNotIn,
  },
  {
    label: intl.formatMessage({ id: 'Load.Filters.OperatorGreaterThan' }),
    value: TextMatch.TextGreater,
  },
  {
    label: intl.formatMessage({
      id: 'Load.Filters.OperatorEqualsOrGreaterThan',
    }),
    value: TextMatch.TextGreaterEqual,
  },
  {
    label: intl.formatMessage({ id: 'Load.Filters.OperatorLesserThan' }),
    value: TextMatch.TextLess,
  },
  {
    label: intl.formatMessage({
      id: 'Load.Filters.OperatorEqualsOrLesserThan',
    }),
    value: TextMatch.TextLessEqual,
  },
]

export const numericMatchOpts = (
  intl: IntlShape,
): Array<MatchOption<NumericMatch>> => [
  {
    label: intl.formatMessage({
      id: 'Load.Filters.OperatorNumericEqualsTo',
    }),
    value: NumericMatch.NumericSame,
  },
  {
    label: intl.formatMessage({
      id: 'Load.Filters.OperatorNumericNotEqualsTo',
    }),
    value: NumericMatch.NumericDifferent,
  },
  {
    label: intl.formatMessage({
      id: 'Load.Filters.OperatorNumericGreaterThan',
    }),
    value: NumericMatch.NumericGreater,
  },
  {
    label: intl.formatMessage({
      id: 'Load.Filters.OperatorNumericGreaterOrEquals',
    }),
    value: NumericMatch.NumericGreaterEqual,
  },
  {
    label: intl.formatMessage({
      id: 'Load.Filters.OperatorNumericLesserThan',
    }),
    value: NumericMatch.NumericLess,
  },
  {
    label: intl.formatMessage({
      id: 'Load.Filters.OperatorNumericLesserOrEquals',
    }),
    value: NumericMatch.NumericLessEqual,
  },
]

export const DATEMATCHGROUPS = {
  FIXED: [
    DateMatch.EqualToToday,
    DateMatch.Before,
    DateMatch.After,
    DateMatch.EqualTo,
  ],
  CALCULATED: [
    DateMatch.PlusXdaysEqualsToToday,
    DateMatch.MinusXdaysEqualsToToday,
    DateMatch.PlusXDaysEarlierThanToday,
    DateMatch.MinusXdaysEarlierThanToday,
    DateMatch.PlusXdaysLaterThanToday,
    DateMatch.MinusXdaysLaterThanToday,
  ],
}

export const dateMatchGroups = (intl: IntlShape): SelectGroup[] => [
  {
    id: 0,
    label: intl.formatMessage({
      id: 'Load.Filters.FixedValue',
    }),
    values: DATEMATCHGROUPS.FIXED,
  },
  {
    id: 1,
    label: intl.formatMessage({
      id: 'Load.Filters.CalculatedValue',
    }),
    values: DATEMATCHGROUPS.CALCULATED,
  },
]

export const dateMatchOpts = (
  intl: IntlShape,
): Array<MatchOption<DateMatch>> => [
  {
    label: intl.formatMessage({ id: 'Load.Filters.OperatorDateEqualsToday' }),
    value: DateMatch.EqualToToday,
  },
  {
    label: intl.formatMessage({ id: 'Load.Filters.OperatorDateBefore' }),
    value: DateMatch.Before,
  },
  {
    label: intl.formatMessage({ id: 'Load.Filters.OperatorDateAfter' }),
    value: DateMatch.After,
  },
  {
    label: intl.formatMessage({ id: 'Load.Filters.OperatorDateEqualsTo' }),
    value: DateMatch.EqualTo,
  },
  {
    label: intl.formatMessage({
      id: 'Load.Filters.OperatorDatePlusInputEqualsToday',
    }),
    value: DateMatch.PlusXdaysEqualsToToday,
  },
  {
    label: intl.formatMessage({
      id: 'Load.Filters.OperatorDateMinusInputEqualsToday',
    }),
    value: DateMatch.MinusXdaysEqualsToToday,
  },
  {
    label: intl.formatMessage({
      id: 'Load.Filters.OperatorDatePlusInputBeforeToday',
    }),
    value: DateMatch.PlusXDaysEarlierThanToday,
  },
  {
    label: intl.formatMessage({
      id: 'Load.Filters.OperatorDateMinusInputBeforeToday',
    }),
    value: DateMatch.MinusXdaysEarlierThanToday,
  },
  {
    label: intl.formatMessage({
      id: 'Load.Filters.OperatorDatePlusInputAfterToday',
    }),
    value: DateMatch.PlusXdaysLaterThanToday,
  },
  {
    label: intl.formatMessage({
      id: 'Load.Filters.OperatorDateMinusInputAfterToday',
    }),
    value: DateMatch.MinusXdaysLaterThanToday,
  },
]

export const activityDateTypeOpts = (
  intl: IntlShape,
): Array<MatchOption<UiActivityDateType>> => [
  {
    label: intl.formatMessage({ id: 'Load.Filters.ActivityDateDetailsAny' }),
    value: UiActivityDateType.AnyMoment,
  },
  {
    label: intl.formatMessage({ id: 'Load.Filters.ActivityDateDetailsRange' }),
    value: UiActivityDateType.DateInterval,
  },
  {
    label: intl.formatMessage({
      id: 'Load.Filters.ActivityDateDetailsLastDays',
    }),
    value: UiActivityDateType.LastDays,
  },
  {
    label: intl.formatMessage({
      id: 'Load.Filters.ActivityDateDetailsLastHours',
    }),
    value: UiActivityDateType.LastHours,
  },
  {
    label: intl.formatMessage({
      id: 'Load.Filters.ActivityDateDetailsExactlyDays',
    }),
    value: UiActivityDateType.ExactDaysAgo,
  },
  {
    label: intl.formatMessage({
      id: 'Load.Filters.ActivityDateDetailsExactlyHours',
    }),
    value: UiActivityDateType.ExactHoursAgo,
  },
]

export const activityDefaultMsgValues = [0, -1, -5]
export const activityDefaultMsgOpts = (intl: IntlShape): SelectOption[] => [
  {
    label: intl.formatMessage({
      id: 'Load.Filters.ActivityAnyMessage',
    }),
    value: 0,
  },
  {
    label: intl.formatMessage({
      id: 'Load.Filters.ActivityLatestSentMessage',
    }),
    value: -1,
  },
  {
    label: intl.formatMessage({
      id: 'Load.Filters.ActivityLatest5SentMessages',
    }),
    value: -5,
  },
]
